import { z } from "zod";
import { browserEnvDefinitions, browserEnvKeys } from "./browser-env-keys.mjs";

export const Environment = {
	develop: "develop",
	development: "development",
	staging: "staging",
	uat: "uat",
	production: "production",
} as const;

export type Environment = (typeof Environment)[keyof typeof Environment];
export type BrowserEnvKey = keyof typeof browserEnvDefinitions;

const environmentSchema = z.enum(Environment);
const schemaByKind = {
	url: z.url(),
	nonEmpty: z.string().min(1),
	// The build-time projection (configs/vite react-app.ts) substitutes "" for
	// every UNSET allowlisted key — for an optional key, empty is absent, and
	// rejecting "" would boot-throw every deploy that legitimately omits the
	// key. Required kinds still fail on
	// "" exactly as they fail on absence.
	optionalNonEmpty: z
		.string()
		.optional()
		.transform((value) => (value === "" ? undefined : value)),
	environment: environmentSchema,
};
type BrowserEnvShape = {
	[K in BrowserEnvKey]: (typeof schemaByKind)[(typeof browserEnvDefinitions)[K]];
};

const browserEnvShape = Object.fromEntries(
	browserEnvKeys.map((key) => [key, schemaByKind[browserEnvDefinitions[key]]]),
) as BrowserEnvShape;

const AXIOM_TRIO_KEYS = ["COMMON_ENV_AXIOM_TOKEN", "COMMON_ENV_AXIOM_ORG_ID", "COMMON_ENV_AXIOM_DATASET"] as const;

/**
 * Strict, dependency-isolated common-env profile for Product v2 browser bundles. The
 * Axiom trio is conditionally required (env-keyed logging policy): `develop` logs to
 * console and may omit it; every other environment ships to Axiom, so a deployed
 * config missing the trio fails validation loudly at build/boot instead of going blind.
 */
export const browserEnvSchema = z.object(browserEnvShape).superRefine((env, ctx) => {
	if (env.COMMON_ENV_ENVIRONMENT === Environment.develop) return;
	for (const key of AXIOM_TRIO_KEYS) {
		if (!env[key]) {
			ctx.addIssue({
				code: "custom",
				path: [key],
				message: `${key} is required when COMMON_ENV_ENVIRONMENT is "${env.COMMON_ENV_ENVIRONMENT}" — only "develop" logs to console without Axiom`,
			});
		}
	}
});
export type BrowserEnvConfig = z.infer<typeof browserEnvSchema>;
export { browserEnvKeys };

function pickBrowserKeys(envSource: Record<string, unknown>): Record<string, unknown> {
	return Object.fromEntries(browserEnvKeys.map((key) => [key, envSource[key]]));
}

/** Strict browser-safe common-env resolution for Product v2 SPAs. */
export function createBrowserEnvConfig(envSource: Record<string, unknown>): BrowserEnvConfig {
	const result = browserEnvSchema.safeParse(pickBrowserKeys(envSource));
	if (!result.success) {
		console.error("❌ Invalid browser environment variables:", z.treeifyError(result.error));
		throw new Error("Invalid browser environment variables");
	}
	return result.data;
}
